import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { FilterSubitemPetaniValues } from '../validation'
import type { LahanPetaniDataType } from './types'
import type { MutationLahanPetaniValues } from './validation'

const QUERY_KEY = (key?: string | Record<string, any>) =>
  ['petani-lahan', key] as const

const getAll = (
  params?: FilterSubitemPetaniValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<LahanPetaniDataType>>
> => ({
  queryKey: QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<LahanPetaniDataType>>
      >(`/kelembagaan/petani/${params?.id_petani}/lahan`, {
        params: {
          ...params,
          id_petani: undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<LahanPetaniDataType>,
  MutationLahanPetaniValues
> => ({
  mutationFn: async (data: MutationLahanPetaniValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<LahanPetaniDataType>
      >(`/kelembagaan/petani/${data?.id_petani}/lahan`, {
        ...data,
        id_jenis_petani: data.id_jenis_petani.value,
        luas_lahan_diusahakan: data.luas_lahan_diusahakan
          ? Number.parseFloat(data.luas_lahan_diusahakan)
          : undefined,
        luas_lahan_dimiliki: data.luas_lahan_dimiliki
          ? Number.parseFloat(data.luas_lahan_dimiliki)
          : undefined,
        id_desa: data.id_desa.value,
        latitude: data.latitude?.toString() ?? null,
        longitude: data.longitude?.toString() ?? null,
        polygon_coordinates:
          data.polygon_coordinates && data.polygon_coordinates.length >= 3
            ? data.polygon_coordinates.map(([latitude, longitude]) => ({
                latitude,
                longitude
              }))
            : undefined,
        komoditas: data.komoditas.map(komoditas => ({
          id: komoditas.id.value,
          id_subsektor: komoditas.id_subsektor.value,
          volume: komoditas.volume ? Number.parseFloat(komoditas.volume) : 0
        }))
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<LahanPetaniDataType>,
  MutationLahanPetaniValues
> => ({
  mutationFn: async (data: MutationLahanPetaniValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<LahanPetaniDataType>
      >(`/kelembagaan/petani/${data?.id_petani}/lahan/${data.id_lahan}`, {
        ...data,
        id_jenis_petani: data.id_jenis_petani.value,
        luas_lahan_diusahakan: data.luas_lahan_diusahakan
          ? Number.parseFloat(data.luas_lahan_diusahakan)
          : undefined,
        luas_lahan_dimiliki: data.luas_lahan_dimiliki
          ? Number.parseFloat(data.luas_lahan_dimiliki)
          : undefined,
        id_desa: data.id_desa.value,
        latitude: data.latitude?.toString() ?? null,
        longitude: data.longitude?.toString() ?? null,
        polygon_coordinates:
          data.polygon_coordinates && data.polygon_coordinates.length >= 3
            ? data.polygon_coordinates.map(([latitude, longitude]) => ({
                latitude,
                longitude
              }))
            : undefined,
        komoditas: data.komoditas.map(komoditas => ({
          id: komoditas.id.value,
          id_subsektor: komoditas.id_subsektor.value,
          volume: komoditas.volume ? Number.parseFloat(komoditas.volume) : 0
        }))
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<LahanPetaniDataType>,
  { id_petani: string; id_data: string }
> => ({
  mutationFn: async (data: { id_petani: string; id_data: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<LahanPetaniDataType>
      >(`/kelembagaan/petani/${data?.id_petani}/lahan/${data?.id_data}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const lahan = {
  queryKey: [QUERY_KEY(undefined)[0]],
  getAll,
  create,
  update,
  deleteData
}
